import CustomUser from "@electricMotorShop/Icons/User";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const buyAgainWishListsParameter: GetWishListsApiParameter = {
    sort: "ModifiedOn DESC",
};

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;

    const wishListsDataView = getWishListsDataView(state, buyAgainWishListsParameter);
    const wishLists = wishListsDataView?.value;
    const buyAgainList = isAuthenticated && wishLists ? wishLists.find(list => list.name === "Buy Again") : undefined;
    const buyAgainListUrl = buyAgainList ? `/my-account/my-lists/Details?id=${buyAgainList.id}` : undefined;

    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        buyAgainListUrl,
        wishListsLoaded: !!wishListsDataView?.value,
        wishListsLoading: !!wishListsDataView?.isLoading,
        isAuthenticated,
        orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
        onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    loadWishLists,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
}


export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            height: 50px;
        `,
    },
    titleClickable: {
        css: css`
            color: ${getColor("secondary.main")};
            height: 50px;
        `,
    },
    titleIcon: {
        size: 24,
        css: css`
            color: ${getColor("secondary.main")};
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 8px;
            max-width: 150px;

            &:hover {
                text-decoration: underline;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
        ellipsis: true,
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            color: ${getColor("secondary.main")};
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    buyAgainListUrl,
    wishListsLoaded,
    wishListsLoading,
    isAuthenticated,
    loadWishLists,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    useEffect(() => {
        if (isAuthenticated && !wishListsLoaded && !wishListsLoading) {
            loadWishLists(buyAgainWishListsParameter);
        }
    }, [isAuthenticated, wishListsLoaded, wishListsLoading, loadWishLists]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, showInventoryToggle, showPricingToggle } = fields;
    let { includeAccountMenu } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = CustomUser;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    const getSignOutMenuItem = (): MappedLink => {
        return {
            title: translate("Sign Out"),
            clickableProps: {
                onClick: () => {
                    signOut();
                },
                href: "#", // need to add to force it to link instead of button
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        if (buyAgainListUrl) {
            const orderHistoryIndex = menuItems.findIndex(item => item.type === "OrderHistoryPage");
            const buyAgainItem: MappedLink = {
                title: translate("Buy Again"),
                url: buyAgainListUrl,
            };
            if (orderHistoryIndex !== -1) {
                menuItems.splice(orderHistoryIndex + 1, 0, buyAgainItem);
            } else {
                menuItems.push(buyAgainItem);
            }
        }

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    if (userName && !currentUserIsGuest) {
        menuItems.push(getSignOutMenuItem());
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    // don't display account menu on mobile
    if (typeof window !== "undefined" && window.innerWidth < theme.breakpoints.values[2]) {
        includeAccountMenu = false;
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {includeAccountMenu && userName && !currentUserIsGuest && (
                <Menu
                    descriptionId="accountMenu"
                    menuItems={menuItems}
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    maxDepth={1}
                    width={300}
                    menuTrigger={
                        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                            {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                            {showLabel && (
                                <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                    {userName}
                                </Typography>
                            )}
                        </Clickable>
                    }
                    cssOverrides={{
                        wrapper: css`
                            flex: 0 1 auto;
                            min-width: 0;
                        `,
                        menu: css`
                            top: 70px;
                            text-align: left;
                            border-radius: 4px;
                            :before {
                                content: "";
                                border-left: 22px solid transparent;
                                border-right: 22px solid transparent;
                                border-bottom: 22px solid ${getColor("common.background")};
                                top: -22px;
                                left: 70px;
                                position: absolute;
                            }
                            :after {
                                content: "";
                                width: 100%;
                                height: 25px;
                                top: -25px;
                                left: 0;
                                position: absolute;
                            }
                        `,
                    }}
                    data-test-selector="header_accountMenu"
                />
            )}
            {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable
                    {...styles.titleClickable}
                    onClick={onSignInHandler}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} src={icon} title={signInStatusText} />}
                    {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
            {/* {userName && !currentUserIsGuest && (
                <Clickable
                    {...styles.signOutClickable}
                    onClick={onSignOutHandler}
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Clickable>
            )} */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
